import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import { Checkbox } from "@mui/material";
import { useStoreActions, useStoreState } from "easy-peasy";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { MESSAGES } from "constants/Messages";
import { get } from "requests";
import DealPayOffTable from "./DealPayOffTable";
import { useSubmissionStore } from "models/applications/SubmissionStore";

interface DealPayOffProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const DealPayOffs: React.FC<DealPayOffProps> = ({ dealApplicationId, setComponentLoading }) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [dealOffersData, setDealOffersData] = useState<any>([]);

  const { updateDealApplicationNo } = useStoreActions((actions: any) => actions.dealApplication);

  // offers store
  const offerDetails = useStoreState((state: any) => state.offerStore.contractTerms);
  const payingOffDeals = offerDetails?.contractTerms?.payingOffDeals;
  const offerStoreRefresh = useStoreActions((actions: any) => actions.offerStore.onInit);

  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<string | null>("amount");
  const setLoading = useStoreActions((actions: any) => actions.offerStore.setLoading);
  const saveFinalOffer = useStoreActions((actions: any) => actions.offerStore.saveContractTerms);
  const getContractTerms = useStoreActions((actions: any) => actions.offerStore.getContractTerms);
  const [refinanceDealsOptions, setRefinanceDealsOptions] = useState<any>([]);
  const [mode, setMode] = useState<string>("");

  //heldback store
  const setSyndicationBankFeeAlert = useStoreActions(
    (actions: any) => actions.heldBackCommissionStore.setSyndicatorBankFeeAlert
  );

  //syndication store
  const updateSyndicatorsPercentage = useStoreActions(
    (actions: any) => actions.syndicationStore.updateSyndicatorsPercentage
  );
  const { dealApplicationData, fetchInitialData } = useSubmissionStore();

  useEffect(() => {
    try {
      setComponentLoading("dealPayoffs", true);
      initialFetchAndPreparePayingOffDealsList();
      if (payingOffDeals) {
        const updatedDeals = payingOffDeals.map((deal: any) => ({
          id: deal.dealId,
          funderName: deal.funderName,
          dealStatus: deal.dealStatus,
          type: "Refinance",
          refinanceAmount: deal.payingoffAmount,
          outstandingBalance: deal.dealOutstandingAmount,
          fundingAmount: deal.parentFundingAmount,
          merchantName: deal.merchantName,
          dealId: deal.dealNo,
          pendingAchDebits: deal.pendingCollectionAmount,
          subtract: deal.subtract,
        }));
        updatedDeals.sort((a: any, b: any) => a.id - b.id);
        setDealOffersData(updatedDeals);
      }
    } finally {
      setComponentLoading("dealPayoffs", false);
    }
  }, [payingOffDeals, dealApplicationData]);

  const isInputColumnDisabled = () => {
    return mode !== "add";
  };

  const columns = [
    {
      Header: "FUNDER NAME",
      accessor: "funderName",
      allowSelection: false,
      align: "left",
      disabled: (): boolean => true,
      width: 300,
      required: false,
    },
    {
      Header: "DEAL STATUS",
      accessor: "dealStatus",
      width: 120,
      disabled: (): boolean => true,
      required: false,
    },
    {
      Header: "TYPE",
      accessor: "type",
      width: 140,
      align: "left",
      disabled: (): boolean => true,
      options: [{ label: "Refinance", value: "Refinance" }],
      required: true,
    },
    {
      Header: "REFINANCE AMOUNT",
      accessor: "refinanceAmount",
      width: 150,
      align: "left",
      Cell: (row: any) => formatCurrencyUSD(row?.row?.refinanceAmount),
    },
    {
      Header: "OUTSTANDING BALANCE",
      accessor: "outstandingBalance",
      width: 150,
      disabled: (): boolean => true,
      required: false,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.outstandingBalance),
    },
    {
      Header: "FUNDING AMOUNT",
      accessor: "fundingAmount",
      width: 150,
      disabled: (): boolean => true,
      required: false,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.fundingAmount),
    },
    {
      Header: "MERCHANT NAME",
      accessor: "merchantName",
      disabled: (): boolean => true,
      width: 300,
      align: "left",
      required: false,
    },
    {
      Header: "DEAL ID",
      accessor: "dealId",
      selector: "dealValue",
      disabled: isInputColumnDisabled,
      required: editingRow?.type === "Refinance" ? true : false,
      options: refinanceDealsOptions,
      width: 250,
    },
    {
      Header: "PENDING ACH DEBITS",
      accessor: "pendingAchDebits",
      width: 150,
      disabled: (): boolean => true,
      required: false,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.pendingAchDebits),
    },
    {
      Header: "SUBTRACT?",
      accessor: "subtract",
      width: 120,
      hidden: true,
      disabled: (): boolean => true,
      Cell: ({ row }: { row: any }) => {
        return (
          <MDBox display={"flex"} justifyContent={"center"}>
            <Checkbox
              checked={row.subtract}
              onChange={(e) => handlePendingACHCheckBox(row, e.target.checked)}
            />
          </MDBox>
        );
      },
    },
  ];

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const handleInputChange = (value: any, field: string) => {
    if (field === "dealId") {
      console.log(value);
      setEditingRow((prev: any) => ({
        ...prev,
        dealValue: value,
        dealId: value.dealNo,
        id: value.dealId,
        funderName: value.funderName,
        dealStatus: value.dealStatus,
        refinanceAmount: value.dealOutstandingAmount,
        outstandingBalance: value.dealOutstandingAmount,
        fundingAmount: value.fundingAmount,
        merchantName: value.merchantName,
      }));
    } else {
      setEditingRow((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  console.log(editingRow);

  const handleSelectRow = (rowKey: number) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(rowKey)
        ? prevSelected.filter((key) => key !== rowKey)
        : [...prevSelected, rowKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === dealOffersData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(dealOffersData.map((row: any) => row.id));
    }
  };

  const handleEditPayOffs = (row: any | null) => {
    setIsEditing(true);
    setEditingRow(row ? { ...row } : {});
  };

  const selectAllRows = (isChecked: boolean) => {
    setSelectedRows(isChecked ? dealOffersData.map((offer: any) => offer.id) : []);
  };

  const getRowStyle = (row: any) => {
    return "regular";
  };

  const renderTotal = (key: any) => {
    let total = 0;

    if (editingRow && editingRow[key]) {
      const editingAmount = parseFloat(editingRow[key]);
      if (!isNaN(editingAmount)) {
        total += editingAmount;
      }
    }

    dealOffersData
      .filter((row: any) => !editingRow || editingRow.id !== row.id)
      .forEach((row: any) => {
        const rowAmount = parseFloat(row[key]);
        if (!isNaN(rowAmount)) {
          total += rowAmount;
        }
      });

    return key.includes("Amount") ? formatCurrencyUSD(total.toFixed(2)) : total.toFixed(2);
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setIsEditing(false);
    setMode("");
  };

  const initialFetchAndPreparePayingOffDealsList = async () => {
    const partyId = dealApplicationData?.partyId;
    if (!partyId) return;

    try {
      const resultData = await get(`dealParty/fetchDealRecordsOfParty/${partyId}`);

      const refiDeals = resultData
        .filter(
          (deal: any) => deal.dealStatus === "Open" && parseFloat(deal.dealOutstandingAmount) > 0
        )
        .map((deal: any) => ({ label: deal.dealNo, value: deal }))
        .sort((a: any, b: any) => (a.label < b.label ? -1 : 1));

      if (refiDeals.length > 0) {
        setRefinanceDealsOptions(refiDeals);
      }
    } catch (error) {
      console.error("Error fetching paying off deal records:", error);
    }
  };

  const deletePayoff = async (rowId: any) => {
    try {
      setLoading(true);

      let size = dealOffersData?.length;
      if (size && size === 1) {
        setSyndicationBankFeeAlert(MESSAGES.REFINANCE_APPLICATION_VALIDATION_ERROR);
      } else {
        const dealPayOffs = offerDetails?.contractTerms?.payingOffDeals;
        const filteredPayOff = dealPayOffs.filter((payOff: any) => payOff.dealId !== rowId);

        const totalPayoffAmount = filteredPayOff.reduce((sum: number, payOff: any) => {
          return sum + (payOff.payingoffAmount || 0);
        }, 0);

        const request = offerDetails;
        request.applicationId = dealApplicationId;
        request.contractTerms.payingOffDeals = filteredPayOff;
        request.contractTerms.parentDealAmount = totalPayoffAmount;
        request.contractTerms.payingOffDealNos = filteredPayOff;
        await saveFinalOffer(request);
        await offerStoreRefresh(dealApplicationId);
        await updateSyndicatorsPercentage({
          applicationId: dealApplicationId,
          reload: true,
        });

        await updateDealApplicationNo({ dealApplicationId: Number(dealApplicationId) });
        initialFetchAndPreparePayingOffDealsList();
        fetchInitialData(String(dealApplicationId));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePendingACHCheckBox = async (row: any, checked: boolean) => {
    try {
      setLoading(true);

      const dealPayOffs = offerDetails?.contractTerms?.payingOffDeals;
      const editedPayOff = dealPayOffs.find((payOff: any) => payOff.dealId === row.id);

      if (row.pendingAchDebits <= 0) {
        return;
      }

      if (row.outstandingBalance <= 0) {
        return;
      }

      if (editedPayOff) {
        let newRefiAmount = 0;

        if (checked) {
          newRefiAmount = row?.outstandingBalance - row?.pendingAchDebits;
        } else {
          newRefiAmount = row?.outstandingBalance;
        }

        if (newRefiAmount < 0) {
          newRefiAmount = 0;
        }

        editedPayOff.payingoffAmount = newRefiAmount;
      }

      const totalPayoffAmount = dealPayOffs.reduce((sum: number, payOff: any) => {
        return sum + (payOff.payingoffAmount || 0);
      }, 0);

      const request = offerDetails;
      request.applicationId = dealApplicationId;
      request.contractTerms.payingOffDeals = dealPayOffs;
      request.contractTerms.parentDealAmount = totalPayoffAmount;
      request.contractTerms.payingOffDealNos = dealPayOffs;

      await saveFinalOffer(request);
      await offerStoreRefresh(dealApplicationId);

      await updateSyndicatorsPercentage({
        applicationId: dealApplicationId,
        reload: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (row: any) => {
    if (!row || !row?.dealId || !row?.refinanceAmount) {
      return;
    }
    try {
      setLoading(true);

      const dealPayOffs = offerDetails?.contractTerms?.payingOffDeals;

      if (mode === "add") {
        const newPayOff = {
          funderName: row.funderName,
          dealStatus: row.dealStatus,
          type: row.type || "Refinance",
          payingoffAmount: row.refinanceAmount,
          outstandingBalance: row.outstandingBalance,
          fundingAmount: row.fundingAmount,
          merchantName: row.merchantName,
          dealId: row.id,
          pendingAchDebits: row.pendingAchDebits,
          subtract: row.subtract || false,
        };

        dealPayOffs.push(newPayOff);
      } else {
        const editedPayOff = dealPayOffs.find((payOff: any) => payOff.dealId === row.id);
        if (editedPayOff) {
          editedPayOff.payingoffAmount = row.refinanceAmount;
        }
      }

      const totalPayoffAmount = dealPayOffs.reduce((sum: number, payOff: any) => {
        return sum + (payOff.payingoffAmount || 0);
      }, 0);

      const request = offerDetails;
      request.applicationId = dealApplicationId;
      request.contractTerms.payingOffDeals = dealPayOffs;
      request.contractTerms.parentDealAmount = totalPayoffAmount;
      request.contractTerms.payingOffDealNos = dealPayOffs;

      await saveFinalOffer(request);
      await offerStoreRefresh(dealApplicationId);

      await updateSyndicatorsPercentage({
        applicationId: dealApplicationId,
        reload: true,
      });
      initialFetchAndPreparePayingOffDealsList();
    } finally {
      setEditingRow(null);
      setIsEditing(false);
      setLoading(false);
      setMode("");
    }
  };

  const handleAddPayoff = () => {
    setMode("add");
    const payOff: any = {
      id: "",
      funderName: "",
      dealStatus: "",
      type: "Refinance",
      refinanceAmount: "",
      outstandingBalance: "",
      fundingAmount: "",
      merchantName: "",
      dealId: "",
      pendingAchDebits: "",
      subtract: false,
    };
    setEditingRow(payOff);
    setIsEditing(true);
  };

  const customButton = (
    <MDButton
      color="primary"
      variant="contained"
      size="medium"
      sx={{ textTransform: "none" }}
      onClick={handleAddPayoff}
    >
      + Add Payoff
    </MDButton>
  );

  const isRowDisabled = (row: any, accessor: any) => {
    if (mode === "add" && accessor === "dealId") {
      return false;
    }

    return true;
  };

  const getInputType = (row: any, accessor: any) => {
    if (accessor === "dealId") {
      return mode === "add" ? "select" : "normal";
    }

    if (accessor === "refinanceAmount") {
      return "amount";
    }

    if (accessor === "outstandingBalance") {
      return "amount";
    }

    if (accessor === "funderName") {
      return "normal";
    }

    if (accessor === "fundingAmount") {
      return "amount";
    }

    if (accessor === "pendingAchDebits") {
      return "amount";
    }

    return "normal";
  };

  const renderTable = () => {
    return (
      <DealPayOffTable
        rowKey={"id"}
        columns={columns}
        data={dealOffersData}
        onDelete={deletePayoff}
        onEdit={handleEditPayOffs}
        mode={mode}
        onSave={onSave}
        onCancel={handleCancelEdit}
        editingRow={editingRow}
        isEditing={isEditing}
        onInputChange={handleInputChange}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        getRowStyle={getRowStyle}
        isActionsDisabled={undefined}
        renderTotal={renderTotal}
        totalColumn={["refinanceAmount"]}
        isRowDisabled={isRowDisabled}
        getInputType={getInputType}
      />
    );
  };

  return (
    <CollapsibleCard header={"Deal Payoffs"} customButton={customButton}>
      <MDBox paddingRight={3}>{renderTable()}</MDBox>
    </CollapsibleCard>
  );
};

export default DealPayOffs;
